import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import FormClientDetails from "./formSections/FormClientDetails.controller"
import { createClient, editClient } from "../../../../services/client.service"
import Divider from "../../../../components/atoms/Divider.atom"
import ProgressSpinner from "../../../../components/atoms/misc/ProgressSpinner.atom"
import {
  CLIENT_INITIAL_STATE,
  FORM_CLIENT_MODE,
} from "../../../../consts/modules/clients.consts"
import useToast from "../../../../hooks/useToast.hook"
import FormClientBudget from "./formSections/FormClientBudget.controller"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import SecondaryButton from "../../../../components/molecules/buttons/SecondaryButton.molecule"
import PrimaryButton from "../../../../components/molecules/buttons/PrimaryButton.molecule"
import { areObjectsEqual } from "../../../../utils/object.utils"
import useClientColorsContext from "../../../../hooks/contextConsumers/useClientColorsContext.hook"
import useReportFiltersContext from "../../../../hooks/contextConsumers/useReportFiltersContext.hook"

const ModalFormClient = ({
  visible,
  setVisible,
  mode = FORM_CLIENT_MODE.CREATE,
  setSelectedClient,
  selectedClient = CLIENT_INITIAL_STATE,
  triggerRefresh,
}) => {
  const [client, setClient] = useState(CLIENT_INITIAL_STATE)
  const [prevClient, setPrevClient] = useState(CLIENT_INITIAL_STATE)
  const [hasClientChanged, setHasClientChanged] = useState(false)
  const [error, setError] = useState({
    details: false,
    budget: false,
  })
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { setErrorMessage } = useToast()
  const isCreate = mode === FORM_CLIENT_MODE.CREATE
  const { refreshColorContext } = useClientColorsContext()
  const { updateClientReportDropdown } = useReportFiltersContext()
  useEffect(() => {
    if (areObjectsEqual(selectedClient, client)) return
    setClient(selectedClient)
    setPrevClient(selectedClient)
  }, [selectedClient])
  useEffect(() => {
    if (client) {
      setHasClientChanged(!areObjectsEqual(client, prevClient))
    }
  }, [client])

  const loadingTemplate = () => {
    return (
      <div
        className="flex flex-column gap-3 p-1 shadow-none justify-content-center align-items-center"
        style={{ height: "500px" }}
      >
        <ProgressSpinner />
      </div>
    )
  }
  const handleSubmit = async () => {
    setSubmitting(true)
    setError({
      details: false,
      budget: false,
    })
    try {
      const service = isCreate ? createClient : editClient
      const hexColor = getComputedStyle(document.documentElement)
        .getPropertyValue(client?.color.match(/--[a-zA-Z0-9-]+/)?.[0])
        .trim()
      if (hexColor) {
        client.color = hexColor
      }
      const formData = new FormData()
        const clientData = {
          ...client,
          budgetType: parseInt(client.budgetType),
          shouldRemoveLogo: !isCreate && client.clientLogo === null
        }
        formData.append("client", JSON.stringify(clientData))
        
        if (client.clientLogo && client.clientLogo instanceof File) {
          formData.append("clientLogo", client.clientLogo)
        }
        
      const response = await service(formData)
      if (!response.success) {
        throw new Error(response.message)
      }
      refreshColorContext()
      updateClientReportDropdown()
      setVisible(false)
      triggerRefresh()
      resetFormState()
    } catch (error) {
      console.error(error)
      setErrorMessage({ message: error.message || "Hubo un error al guardar." })
    } finally {
      setSubmitting(false)
    }
  }

  const resetFormState = () => {
    setClient(CLIENT_INITIAL_STATE)
    setPrevClient(CLIENT_INITIAL_STATE)
    setError({
      details: false,
      budget: false,
    })
    setVisible(false)
    setSelectedClient && setSelectedClient(null)
  }

  const isClientIncomplete = (client) => {
    if (!client) return true
    const expectedFields = ["active", "budgetType", "code", "name", "color"]
    const clientKeys = Object.keys(client)
    const hasAllFields = expectedFields.every((field) =>
      clientKeys.includes(field)
    )
    if (!hasAllFields) return true
    const hasEmptyFields = expectedFields.some((field) => {
      const value = client[field]
      return (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0)
      )
    })
    if (hasEmptyFields) return true
    return false
  }

  const ModalFooterTemplate = () => {
    return (
      <div>
        <SecondaryButton
          rounded
          icon="pi pi-times"
          label="Cancelar"
          onClick={() => setVisible(false)}
        />
        <PrimaryButton
          rounded
          icon={`pi ${submitting ? "pi-spin pi-spinner" : "pi-check"}`}
          label={"Aceptar"}
          onClick={handleSubmit}
          disabled={
            submitting ||
            isClientIncomplete(client) ||
            Object.values(error).some((value) => value) ||
            !hasClientChanged
          }
        />
      </div>
    )
  }
  return (
    <ModalForm
      visible={visible}
      setVisible={setVisible}
      headerIconClass={`pi pi-${isCreate ? "plus" : "pencil"} text-2xl`}
      headerTitle={`${isCreate ? "Crear" : "Editar"} cliente`}
      service={handleSubmit}
      className="client-modal-form create-task-admin"
      id="client-form"
      cleanUp={resetFormState}
      footer={ModalFooterTemplate()}
    >
      {loading ?
        loadingTemplate()
      : <>
          <FormClientDetails
            client={client}
            setClient={setClient}
            isCreate={isCreate}
            setError={setError}
          />
          {isCreate && (
            <>
              <Divider className="mt-0 mb-3" stroke="sm" />
              <FormClientBudget
                client={client}
                setClient={setClient}
                setError={setError}
              />
            </>
          )}
        </>
      }
    </ModalForm>
  )
}

export default ModalFormClient

ModalFormClient.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_CLIENT_MODE)),
  setSelectedClient: PropTypes.func,
}
